/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */

import {ITokenProvider, ITokenResponse} from "@fluidframework/routerlicious-driver";
import axios from "axios";
import {AzureMember} from "@fluidframework/azure-client";
import {isExpiredToken} from "../utils/tokenUtils";
import {acquireToken} from "../../../../MsalUtils";

/**
 * Token Provider implementation for connecting to an Azure Function endpoint for
 * Azure Fluid Relay token resolution.
 */
export class AzureFunctionTokenProviderSecure implements ITokenProvider {
    private fluidToken: string = '';
    private authToken: string = '';

    /**
     * Creates a new instance using configuration parameters.
     * @param azFunctionUrl - URL to Azure Function endpoint
     * @param user - User object
     */
    constructor(
        private readonly azFunctionUrl: string,
        private readonly setDocumentId?: (documentId: string) => void,
        private readonly user?: Pick<AzureMember, "userId" | "userName" | "additionalDetails">,
    ) {
    }

    public async fetchOrdererToken(tenantId: string, documentId?: string): Promise<ITokenResponse> {
        return this.fetchStorageToken(tenantId, documentId!);
    }

    public async fetchStorageToken(tenantId: string, documentId: string): Promise<ITokenResponse> {
        return this.getToken(tenantId, documentId);
    }

    public async documentPostCreateCallback(documentId: string, creationToken: string): Promise<void> {
        if (this.setDocumentId) {
            this.setDocumentId(documentId)
        }
    }

    private async getToken(tenantId: string, documentId?: string): Promise<ITokenResponse> {
        if (!isExpiredToken(this.fluidToken)) {
            return {
                fromCache: true,
                jwt: this.fluidToken,
            };
        }

        if (isExpiredToken(this.authToken)) {
            this.authToken = await acquireToken();
        }

        try {
            const response = await axios.get(this.azFunctionUrl, {
                headers: {
                    Authorization: `Bearer ${this.authToken}`
                },
                params: {
                    tenantId,
                    documentId,
                    userId: this.user?.userId,
                    userName: this.user?.userName,
                    additionalDetails: this.user?.additionalDetails,
                },
            });

            this.fluidToken = response.data;
        } catch (error) {
            console.error('Could not fetch meeting details', (error as Error).message);
        }

        return {
            fromCache: false,
            jwt: this.fluidToken,
        };
    }
}
